import { useEffect, useRef } from "react"

function pngFor(imageUrl) {
  return imageUrl.replace(/\.svg(\?.*)?$/, ".png$1")
}

// Native <dialog>-based modal that displays a single QR code at a size large
// enough to actually be scanned/read. Clicking the row thumbnail in the
// dashboard opens this; ESC and backdrop click both close.
export default function QrPreviewModal({ open, onClose, code }) {
  const dialogRef = useRef(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    function handleCancel(e) {
      e.preventDefault()
      onClose()
    }
    function handleClick(e) {
      if (e.target === dialog) onClose()
    }
    dialog.addEventListener("cancel", handleCancel)
    dialog.addEventListener("click", handleClick)
    return () => {
      dialog.removeEventListener("cancel", handleCancel)
      dialog.removeEventListener("click", handleClick)
    }
  }, [onClose])

  if (!code) return null

  const filenameBase = `tango-qr-${code.token}`
  const shortLabel = (code.short_url || "").replace(/^https?:\/\//, "")

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={`qr-modal-title-${code.token}`}
      className="p-0 m-auto rounded-lg bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm shadow-xl backdrop:bg-coal/60 max-w-[90vw] max-h-[90vh] overflow-visible"
    >
      <div className="p-6 flex flex-col items-center gap-4 min-w-[20rem]">
        <div className="w-full flex justify-between items-center gap-3">
          <span
            id={`qr-modal-title-${code.token}`}
            className="font-mono text-sm text-coal dark:text-cloud truncate"
          >
            {shortLabel}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition bg-transparent border-0 p-1 cursor-pointer flex-shrink-0"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <img
          src={code.image_url}
          alt={`QR code for ${shortLabel}`}
          className="w-[min(70vh,28rem)] h-[min(70vh,28rem)] border border-[color:var(--color-border)] dark:border-storm rounded bg-white dark:bg-midnight"
        />

        <div className="text-sm text-[color:var(--color-muted)] dark:text-muted-light truncate max-w-full">
          → {code.destination_url}
        </div>

        <div className="flex gap-2">
          <a
            href={code.image_url}
            download={`${filenameBase}.svg`}
            className="border border-voltage text-voltage rounded px-3 py-1.5 text-xs font-medium hover:bg-voltage hover:text-coal transition"
          >
            SVG
          </a>
          <a
            href={pngFor(code.image_url)}
            download={`${filenameBase}.png`}
            className="border border-voltage text-voltage rounded px-3 py-1.5 text-xs font-medium hover:bg-voltage hover:text-coal transition"
          >
            PNG
          </a>
        </div>
      </div>
    </dialog>
  )
}
